import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import Navigation from "../components/landing/Navigation";
import { Footer } from "../components/landing/Footer";

const RELEASES = [
  {
    version: "v1.0",
    date: "February 19, 2026",
    tag: "Public launch",
    summary:
      "Voxinity AI is now generally available for marketing agencies, with a public marketplace listing on LeadConnector.",
    highlights: [
      "LeadConnector Marketplace integration with full OAuth (location + agency installs).",
      "AI voice agents on ElevenLabs ConvAI with sub-second barge-in and 30+ languages.",
      "AI SMS and chat-widget agents that share the same prompt, knowledge base, and tools as the voice agent.",
      "BYOK telephony for Twilio and Telnyx — no per-minute markup.",
      "Per-call, per-message billing on a dollar balance with hard-stop at $0 and a unified low-balance banner under $3.",
      "Agency workspace, sub-accounts, white-label re-billing, and role-based team access (Owner / Admin / Moderator / Viewer).",
      "Public Help Center, Support, Status (UptimeRobot-backed), Privacy Policy, Terms, and Security pages.",
      "Post-call SMS follow-up classifier (BOOKED / NO_ANSWER / VOICEMAIL / CALLBACK / NOT_INTERESTED / SKIP) with per-sub-account prompt overrides.",
      "Chat widget builder with bubble or rectangle launcher, auto-open, popup teasers, light/dark/auto theme, and per-domain origin allowlist.",
      "LeadConnector marketplace uninstall webhook handler: revokes tokens, halts background sync, and flips the integration card to Disconnected in real time.",
    ],
  },
];

const ChangelogPage = () => {
  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains("light");
    if (wasLight) {
      html.classList.remove("light");
      html.classList.add("dark");
    }
    window.scrollTo(0, 0);
    return () => {
      if (wasLight) {
        html.classList.remove("dark");
        html.classList.add("light");
      }
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-space-900 relative"
      data-testid="changelog-page"
    >
      <Navigation />

      <main className="relative pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-400 transition-colors mb-8"
            data-testid="changelog-back-link"
          >
            <ArrowLeft size={16} />
            Back to home
          </Link>

          <header className="mb-12 border-b border-white/10 pb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-400/10 border border-teal-400/20 text-teal-400 text-xs uppercase tracking-wider mb-4">
              <Sparkles size={14} />
              Product Updates
            </div>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl text-white mb-4">
              Changelog
            </h1>
            <p className="text-gray-400 text-base max-w-2xl">
              New features, improvements, and bug fixes shipped to the
              Voxinity AI platform. Subscribe via our{" "}
              <Link to="/support" className="text-teal-400 hover:underline">
                support page
              </Link>{" "}
              if you&apos;d like release notes emailed.
            </p>
          </header>

          <div className="space-y-12">
            {RELEASES.map((release) => (
              <article
                key={release.version}
                className="relative pl-6 border-l border-white/10"
                data-testid={`changelog-entry-${release.version.toLowerCase()}`}
              >
                <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-teal-400 ring-4 ring-teal-400/10" />
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                  <h2 className="font-display font-semibold text-2xl text-white">
                    {release.version}
                  </h2>
                  <span className="text-sm text-gray-500">{release.date}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-teal-400/10 border border-teal-400/20 text-teal-400 uppercase tracking-wider">
                    {release.tag}
                  </span>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4">
                  {release.summary}
                </p>
                <ul className="space-y-2">
                  {release.highlights.map((h, i) => (
                    <li
                      key={i}
                      className="flex gap-3 text-sm text-gray-400 leading-relaxed"
                    >
                      <span className="text-teal-400 mt-1">▸</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="mt-16 p-5 rounded-xl border border-white/10 bg-white/[0.02] text-sm text-gray-400">
            <p>
              Looking for older entries? This is our first public release —
              we&apos;ll publish a new entry every time we ship a
              user-visible feature, breaking change, or significant
              improvement.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ChangelogPage;
